import { BlockType, PermissionRole, PermissionType } from '@next-space/fe-api-idl';
import type { FC, MutableRefObject } from 'react';
import { useCallback, useRef } from 'react';
import { Button } from 'src/common/components/button';
import { Input } from 'src/common/components/input';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { Tab } from 'src/common/components/tab/tab';
import { TabPane } from 'src/common/components/tab/tab-panel';
import { UNTITLED, UNTITLED_DRIVE } from 'src/common/const';
import { textToSegments } from 'src/editor/utils/editor';
import { useCopyBlockRecord } from 'src/hooks/block/use-copy-block-record';
import { useAsyncTransaction } from 'src/hooks/use-transaction';
import { useCurrentUser } from 'src/hooks/user';
import { addBlock } from 'src/redux/managers/block/add';
import { moveBlock } from 'src/redux/managers/block/move';
import { setBlockPermission } from 'src/redux/managers/block/update-permission';
import { cache } from 'src/redux/store';
import type { NextBlock, SelectBlock } from 'src/redux/types';
import { dataNeedForCopy } from 'src/utils/copy-utils';
import { idsToSelectedBlocks, selectedBlocksToIds } from 'src/utils/select-block-util';

export const useMoveToNew = () => {
  const openModal = useOpenModal();
  const asyncTransaction = useAsyncTransaction();
  const value = useRef<string>('');
  const currentUser = useCurrentUser();
  const copyBlockRecord = useCopyBlockRecord();

  const moveToNew = (
    block: NextBlock,
    options?: {
      copyToSpace?: string;
      callback?: (id: string) => void;
      copyAndMove?: {
        syncId?: string;
        viewId?: string;
      };
    }
  ) => {
    const { copyToSpace, callback, copyAndMove } = options || {};
    const onCreate = (isFolder?: boolean) => {
      let uuid = '';

      asyncTransaction(async () => {
        uuid = addBlock(
          {
            type: isFolder ? BlockType.FOLDER : BlockType.PAGE,
            data: { segments: textToSegments(value.current) },
          },
          { parentId: copyToSpace || block.spaceId, last: true }
        );
        setBlockPermission(uuid, {
          type: PermissionType.USER,
          userId: currentUser.uuid,
          role: PermissionRole.EDITOR,
        });
        if (uuid) {
          if (copyAndMove) {
            await copyBlockRecord(
              idsToSelectedBlocks(selectedBlocksToIds(cache.ui.selectedBlocks)),
              uuid,
              undefined,
              {
                effects: (effectsBlocks: SelectBlock[]) => {
                  moveBlock(selectedBlocksToIds(effectsBlocks), {
                    parentId: uuid,
                    last: true,
                  });
                  message.success('已完成拷贝副本');
                },
              }
            );
          } else if (copyToSpace) {
            const rest = dataNeedForCopy(block);
            addBlock(rest, { parentId: uuid, last: true });
          } else {
            const ids = cache.ui.selectedBlocks.length
              ? selectedBlocksToIds(cache.ui.selectedBlocks)
              : [block.uuid];
            moveBlock(ids, { parentId: uuid, last: true });
          }
        }
      });

      callback?.(uuid);
    };

    openModal.modal({
      content({ onCloseModal }) {
        const props = { onCloseModal, onCreate, value };
        return (
          <div className="next-modal w-[370px]">
            <Tab>
              <TabPane title="文档页面">
                <CreatePage {...props} />
              </TabPane>
              <TabPane title="文件夹页面">
                <CreatePage {...props} isFolder />
              </TabPane>
            </Tab>
          </div>
        );
      },
    });
  };

  return useCallback(moveToNew, [asyncTransaction, copyBlockRecord, currentUser.uuid, openModal]);
};

interface Props {
  onCloseModal: () => void;
  onCreate: (isFolder?: boolean) => void;
  value: MutableRefObject<string>;
  isFolder?: boolean;
}

const CreatePage: FC<Props> = ({ onCloseModal, onCreate, value, isFolder }) => {
  return (
    <>
      <Input
        autoFocus
        className="h-8 my-4 mx-2.5"
        placeholder={isFolder ? UNTITLED_DRIVE : UNTITLED}
        onChange={(v) => (value.current = v)}
      />

      <div className="flex pb-4 mr-2.5 justify-end">
        <Button className="mr-2.5" onClick={() => onCloseModal()} colorType="secondary">
          取消
        </Button>
        <Button
          colorType="active"
          onClick={() => {
            onCreate(isFolder);
            onCloseModal();
          }}
        >
          创建并移动
        </Button>
      </div>
    </>
  );
};
